import "reflect-metadata";
import { bound } from "@frui.ts/helpers";
import { Router } from "@frui.ts/screens/dist";
import { plainToClass } from "class-transformer";
import { action, computed, observable, reaction } from "mobx";
import debounce from "debounce-promise";
import { onlyResolvesLast } from "awesome-only-resolves-last-promise/dist";
import {
  defaultExemptionReason,
  foreignExemptionReasons,
  NoticePowerOfAttorneyRules,
  NoticeTechnicalLicenseRules as NoticeTechnicalLicenseRulesWithId,
} from "models/exemption";
import NiaResponse from "models/niaResponse";
import ExemptionReasonResponse from "entities/exemptionReasonResponse";
import PossibleExemptionRegistrationMethod from "entities/possibleExemptionRegistrationMethod";
import EnumService from "services/enumService";
import EventBus, {
  exemptionCreationFailed,
  exemptionDocumentsUploadFailed,
  exemptionDocumentsUploadSuccess,
  exemptionISDSConceptStatusCheckFailure,
  exemptionISDSConceptStatusCheckSuccess,
  exemptionISDSSessionIdHandled,
  exemptionNIAResponseHandled,
} from "services/eventBus";
import { SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY } from "services/omissionService";
import ExemptionService from "services/exemptionService";
import LocalizationService from "services/localizationService";
import NiaService, { Notice } from "services/NIA/niaService";
import AppRouter from "services/appRouter";
import ISDSService from "services/ISDS/isdsService";
import OutageService from "services/outageService";
import VehicleInfoViewModel from "viewModels/common/vehicleInfoViewModel";
import OutagesViewModel from "viewModels/common/outagesViewModel";
import serializeNotice from "utils/serializeNotice";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import NoticeViewModel from "../noticeViewModel";
import OperatorViewModel from "../components/operator/operatorViewModel";
import ApplicantPersonViewModel from "../components/applicantPersonViewModel";
import { repeatIfFailed } from "services/requestRecoveryService";
import VehicleChargingInstructionsResponse from "entities/vehicleChargingInstructionsResponse";
import ExemptionApplicationProductionResponse from "entities/exemptionApplicationProductionResponse";
import ChargeRegistrationService from "services/chargeRegistrationService";
import { PersistedBase64 } from "models/notice";
import EshopError from "models/eshopError";

export const ExemptionNoticeNavigationName = "exemption_notice";

@Router.registerRoute({ route: ExemptionNoticeNavigationName, name: Router.Self })
export default class ExemptionViewModel extends NoticeViewModel {
  @observable showTooltip = false;
  @observable vehicleInfoViewModel: VehicleInfoViewModel;
  @observable applicantPersonViewModel: ApplicantPersonViewModel;
  @observable operatorInputViewModel: OperatorViewModel;
  @observable allowCreateExemption = false;
  @observable loadingInstruction = false;

  private debouncedLookForChargingInstructions = debounce(this.lookForChargingInstructions, 400);
  private lastResolveFindChargingInstructions = onlyResolvesLast(this.findChargingInstructions);

  constructor(
    public localizationService: LocalizationService,
    protected enumService: EnumService,
    protected niaService: NiaService,
    protected isdsService: ISDSService,
    protected outageService: OutageService,
    protected eventBus: EventBus,
    protected service: ExemptionService,
    protected router: AppRouter,
    protected outagesViewModelFactory: ReturnType<typeof OutagesViewModel.Factory>,
    private chargeRegistrationService: ChargeRegistrationService,
    private vehicleInfoVMFactory: ReturnType<typeof VehicleInfoViewModel.Factory>,
    private applicantPersonInputVMFactory: ReturnType<typeof ApplicantPersonViewModel.Factory>,
    private operatorInputVmFactory: ReturnType<typeof OperatorViewModel.Factory>
  ) {
    super(
      localizationService,
      enumService,
      niaService,
      isdsService,
      outageService,
      eventBus,
      service,
      router,
      outagesViewModelFactory
    );
    this.navigationName = ExemptionNoticeNavigationName;
    this.createChildren();
    this.unsubscriptions.push(
      this.eventBus.subscribe(exemptionCreationFailed, this.generalOperationFailed),
      this.eventBus.subscribe(exemptionDocumentsUploadFailed, this.isdsOperationFailed),
      this.eventBus.subscribe(exemptionDocumentsUploadSuccess, this.isdsUploadOperationSuccess),
      this.eventBus.subscribe(exemptionISDSConceptStatusCheckSuccess, this.isdsConceptStatusCheck),
      this.eventBus.subscribe(exemptionISDSConceptStatusCheckFailure, this.isdsConceptFailure),
      this.eventBus.subscribe(exemptionNIAResponseHandled, this.NiaResponseHandler),
      this.eventBus.subscribe(exemptionISDSSessionIdHandled, this.ISDSSessionIdHandler)
    );
    this.setDefaultCountryId();
    this.attachValidators();
  }

  protected onInitialize(): Promise<any> {
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
    this.service.exemptionApplicationProduction = undefined;
    this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    this.unsubscriptions.push(
      reaction(
        () => [
          this.vehicleInfoViewModel.item.countryId,
          this.vehicleInfoViewModel.item.licensePlate,
          this.notice.exemptionReasonId,
        ],
        () => {
          this.chargingInstructionsReaction();
        },
        {
          fireImmediately: true,
        }
      )
    );
    return super.onInitialize();
  }

  protected onActivate() {
    this.unsubscriptions.push(
      reaction(() => this.notice.exemptionReasonId, this.persistNotice),
      reaction(() => this.notice.vehicle?.countryId, this.onCountryChange)
    );
    return super.onActivate();
  }

  protected createChildren() {
    this.children.clear();
    this.children.push(
      (this.vehicleInfoViewModel = this.vehicleInfoVMFactory(this.notice.vehicle!, {
        attachValidator: true,
        fixedLicensePlateSize: false,
        initByLocalization: true,
      })),
      (this.operatorInputViewModel = this.operatorInputVmFactory(
        this.notice,
        "exemption",
        this.persistNotice,
        this.showBirthdate()
      )),
      (this.applicantPersonViewModel = this.applicantPersonInputVMFactory(this.notice.applicant, this.GDPR, this.showBirthdate()))
    );
  }

  protected attachValidators() {
    attachAutomaticBlurValidator(this.notice, {
      ...this.notice.rules,
      ...NoticePowerOfAttorneyRules,
      ...NoticeTechnicalLicenseRulesWithId,
    });
  }

  async navigate(subPath: string | undefined, params: any) {
    if (!params) this.eventBus.publish(exemptionISDSSessionIdHandled({ sessionId: "" }));

    if (params.sessionId) {
      if (sessionStorage.getItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY)) {
        sessionStorage.removeItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY);
        await this.isdsService.conceptStatus(params.sessionId, Notice.EXEMPTION);
        return;
      }
      this.eventBus.publish(exemptionISDSSessionIdHandled({ sessionId: params.sessionId }));
    } else if (params.response) {
      const response = params.response;

      if (response && this.notice && this.notice.applicant) {
        const urlDecoded = decodeURI(response);
        const base64Decoded = atob(urlDecoded);
        const niaResponse = plainToClass(NiaResponse, JSON.parse(base64Decoded));

        if (!niaResponse) return;

        this.eventBus.publish(exemptionNIAResponseHandled({ niaResponse }));
      }
    }
  }

  @computed
  get notice() {
    return this.service.notice;
  }

  @computed
  get loadingData() {
    return this.loadingInstruction;
  }

  @computed
  get czechCountryId() {
    return this.enumService.values("countries").find(item => item.code === "CZ")?.id;
  }

  @computed
  get reasonsEnum(): ExemptionReasonResponse[] {
    return this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
  }

  @computed
  get vehicleChargingInstructions(): VehicleChargingInstructionsResponse | undefined {
    return this.chargeRegistrationService.vehicleChargingInstructions;
  }

  @computed
  get exemptionApplicationProduction(): ExemptionApplicationProductionResponse | undefined {
    return this.service.exemptionApplicationProduction;
  }

  @computed
  get reasons() {
    return this.reasonsEnum
      .filter(reason => {
        return (
          reason.description !== undefined &&
          reason.exemptionReason !== undefined &&
          reason.possibleRegistrationMethods.includes(PossibleExemptionRegistrationMethod.Notification)
        );
      })
      .filter(reason => {
        if (this.czechCountryId === this.notice.vehicle?.countryId) {
          return true;
        } else {
          return foreignExemptionReasons.includes(`${reason.exemptionReason}`);
        }
      })
      .map(reason => ({
        id: reason.id!,
        label: reason.description!,
      }));
  }

  @bound
  translateGeneral(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`exemption.${code}`, undefined, disableUnbreakableRules);
  }

  @bound
  translateAttribute(code: string) {
    return this.localizationService.translateAttribute("exemption", code);
  }

  @bound
  protected async persistNotice() {
    sessionStorage.setItem(Notice.EXEMPTION, serializeNotice(this.notice));

    await super.persistNotice();
  }

  @bound
  private onCountryChange() {
    const reason = this.reasonsEnum.find(reason => reason.exemptionReason === this.notice.exemptionReasonId)?.exemptionReason;
    if (
      (this.notice.vehicle?.countryId !== this.czechCountryId &&
        this.notice.exemptionReasonId &&
        reason &&
        !foreignExemptionReasons.includes(reason)) ||
      !reason
    ) {
      this.notice.exemptionReasonId = defaultExemptionReason;
      this.showTooltip = true;
      setTimeout(() => {
        this.showTooltip = false;
      }, 6000);
    }
  }

  @bound
  private async chargingInstructionsReaction() {
    this.allowCreateExemption = false;
    this.service.exemptionApplicationProduction = undefined;
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
    if (this.notice.vehicle?.countryId && this.notice.vehicle?.licensePlate) {
      this.notice.vehicle.countryId = this.vehicleInfoViewModel.item.countryId;
      this.notice.vehicle.licensePlate = this.vehicleInfoViewModel.item.licensePlate;
      try {
        this.loadingInstruction = true;
        await this.debouncedLookForChargingInstructions();
        this.loadingInstruction = false;
      } catch (e) {
        this.loadingInstruction = false;
        // Silent catch - background task with auto recovery mechanism
      }
    }
  }

  @bound
  private findChargingInstructions(countryId: string, licensePlate: string) {
    return this.chargeRegistrationService.getChargingInstructions(countryId, licensePlate);
  }

  @bound
  translateWarningReason(code: string, possibleExemptionReasonIds?: string[]): string {
    if (code === "IDENTIFIED_POSSIBLE_EXEMPTION") {
      if (!possibleExemptionReasonIds || possibleExemptionReasonIds.length < 1) {
        return this.localizationService.translateGeneral("errors.generic");
      }
      const possibleExemptionReasonId = possibleExemptionReasonIds[0];
      const exemptionReasons = this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
      const matchingReason = exemptionReasons.find(x => x.id === possibleExemptionReasonId);
      switch (matchingReason?.exemptionReason) {
        case "C":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_C", possibleExemptionReasonIds);
        case "P":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_P", possibleExemptionReasonIds);
        case "O":
        case "O1":
        case "O2":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_O", possibleExemptionReasonIds);
        default:
          return this.localizationService.translateGeneral("errors.generic");
      }
    }
    return this.localizationService.translateGeneral(`eshop.order.license.exemptionReasonWarnings.${code}`, undefined, false);
  }

  @action.bound
  @repeatIfFailed
  private async lookForChargingInstructions() {
    const countryId = this.notice.vehicle?.countryId;
    const licensePlate = this.notice.vehicle?.licensePlate;

    if (!countryId || !licensePlate) return;

    try {
      await this.lastResolveFindChargingInstructions(countryId, licensePlate);
    } catch (e) {
      throw new Error("Vehicle instructions check failed");
    }
  }

  @action.bound
  protected isdsConceptStatusCheck(data: { type: string; payload: { success: boolean; notice: Notice } }) {
    if (data.payload.success && data.payload.notice) {
      this.router.navigate("exemption_documents", undefined, { notice: data.payload.notice });
    }
  }

  @action.bound
  protected isdsUploadOperationSuccess(data: { type: string; payload: { conceptIdent: string } }) {
    super.isdsUploadOperationSuccess(data);
    this.isdsService.redirectToISDSConcept(Notice.EXEMPTION, data.payload.conceptIdent);
  }

  @action.bound
  public async createNotice(): Promise<string | undefined> {
    this.errorMessage = undefined;
    const response = (await this.service.getProduction(this.notice)) as any;

    if (response.errors) {
      this.creationLoadingModalToggle(false);
      this.errorMessage = this.localizationService.translateGeneral("errors.invalidData");
      return;
    }

    if (this.service.exemptionApplicationProduction?.warningReasons.length && !this.allowCreateExemption) {
      this.allowCreateExemption = true;

      return Promise.resolve(undefined);
    }
    return this.service.createExemption(this.notice);
  }

  @action.bound
  public uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]): Promise<void> | undefined {
    this.errorMessage = undefined;
    return this.service.uploadDocuments(ISDSSessionId, files);
  }

  @action.bound
  public redirectToISDS(): void {
    this.errorMessage = undefined;
    this.creationLoadingModalToggle(false);
    this.isdsService.redirectToISDS(Notice.EXEMPTION);
  }

  @action.bound
  public redirectToNia() {
    this.errorMessage = undefined;
    this.creationLoadingModalToggle(false);
    this.niaService.redirectToNia(Notice.EXEMPTION);
  }
}
